import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  Card,
  CardBody,
  Col,
  Container,
  Form,
  FormFeedback,
  FormGroup,
  Input,
  Label,
  Row,
} from "reactstrap";
import * as Yup from "yup";
import { useFormik } from "formik";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import PhoneInput, { isValidPhoneNumber } from "react-phone-number-input";
import { get, post, put } from "helpers/api_helper";
import * as api from "../../constants/api_constants";
import {
  GET_ALL_CONTACTS,
  GET_CONTACT_BY_ID,
  CREATE_CONTACT,
  UPDATE_CONTACT_DATA_BY_ID,
  GET_ALL_VENDORS_LIGHT_API,
  GET_ALL_CUSTOMERS_LIGHT_API,
} from "constants/api_constants";
import {
  getAllCountries,
  getCitiesForState,
  getStatesForCountry,
} from "helpers/utils/locationUtil";
import { PageSubHeader } from "components/CommonLayouts/PageSubHeader";
import Footer from "components/VerticalLayout/Footer";
import { showErrorToast, showSuccessToast } from "helpers/toastMessages";
import secureLocalStorage from "react-secure-storage";
import { charactersOnly, minNameCharacters } from "helpers/validations/regex";

const LoadingSpinner = () => (
  <div className="spinner-border text-primary" role="status">
    <span className="sr-only">Loading...</span>
  </div>
);

function AddContact() {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const [relation, setRelation] = useState("");
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [apiStatus, setApiStatus] = useState({ loading: false });
  const [fetchContactFailed, setFetchContactFailed] = useState(false);

  const storedUser = secureLocalStorage.getItem("authUser");
  const user = JSON.parse(storedUser || "{}");
  const updatedDate = useMemo(() => new Date().toISOString(), []);
  const mode = id ? "edit" : "add";

  const initialValues = useMemo(
    () => ({
      contactRelatedTo: "",
      customerName: "",
      customerId: "",
      vendorName: "",
      vendorId: "",
      contactId: id || "",
      firstName: "",
      lastName: "",
      department: "",
      designation: "",
      primaryContact: false,
      status: "active",
      contactType: "",
      notes: "",
      createdBy: user?.email || "unknown",
      createdDate: mode === "edit" ? updatedDate : "",
      updatedDate: mode === "edit" ? updatedDate : "",
      email: "",
      mobile: "",
      landline: "",
      linkedIn: "",
      country: "",
      province: "",
      city: "",
      postalCode: "",
      address: "",
      isNavigate: false,
    }),
    [id, user?.email, mode, updatedDate]
  );

  const validation = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema: Yup.object({
      contactRelatedTo: Yup.string().required("Please select a relation"),
      customerId: Yup.string().when("contactRelatedTo", {
        is: "customer",
        then: (schema) => schema.required("Please select a customer"),
      }),
      vendorId: Yup.string().when("contactRelatedTo", {
        is: "vendor",
        then: (schema) => schema.required("Please select a vendor"),
      }),
      firstName: Yup.string()
        .required("Please enter first name")
        .min(
          minNameCharacters,
          `First Name must be at least ${minNameCharacters} characters`
        )
        .matches(charactersOnly, "First Name should only contain characters"),
      lastName: Yup.string()
        .required("Please enter last name")
        .min(
          minNameCharacters,
          `Last Name must be at least ${minNameCharacters} characters`
        )
        .matches(charactersOnly, "Last Name should only contain characters"),
      department: Yup.string(),
      designation: Yup.string(),
      primaryContact: Yup.boolean(),
      status: Yup.string().required("Please select status"),
      contactType: Yup.string().required("Please select contact type"),
      notes: Yup.string(),
      email: Yup.string()
        .email("Invalid email address")
        .required("Please enter email"),
      mobile: Yup.string()
        .test(
          "isValidPhone",
          "Invalid phone number format",
          (value) => !value || isValidPhoneNumber(value)
        )
        .required("Mobile number is required"),
      landline: Yup.string().test(
        "isValidPhone",
        "Invalid phone number format",
        (value) => !value || isValidPhoneNumber(value)
      ),
      linkedIn: Yup.string().url("Invalid LinkedIn URL"),
      country: Yup.string().required("Please select a country"),
      province: Yup.string().required("Please select a province"),
      city: Yup.string().required("Please select a city"),
      postalCode: Yup.string().required("Please enter postal code"),
      address: Yup.string().required("Please enter address"),
    }),
    onSubmit: async (values) => {
      try {
        setApiStatus((prev) => ({ ...prev, loading: true }));
       
        const payload = {
          contactTo: {
            subscriptionId: 102,
            customerId:
              values.contactRelatedTo === "customer" ? values.customerId : null,
            vendorId:
              values.contactRelatedTo === "vendor" ? values.vendorId : null,
            contactRelatedTo: values.contactRelatedTo,
            customerName:
              values.contactRelatedTo === "customer"
                ? values.customerName
                : null,
            vendorName:
              values.contactRelatedTo === "vendor" ? values.vendorName : null,
            firstName: values.firstName,
            lastName: values.lastName,
            designation: values.designation,
            department: values.department,
            status: values.status === "active" ? 1 : 0,
            primaryContact: values.primaryContact ? "YES" : "NO",
            contactType: values.contactType,
            notes: values.notes,
            email: values.email,
            phoneNumber: values.mobile,
            landLine: values.landline,
            linkedInUrl: values.linkedIn,
            country: values.country,
            province: values.province,
            city: values.city,
            postalCode: values.postalCode,
            address: values.address,
            createdBy: user?.email || "unknown",
            updatedBy: user?.email || "unknown",
            createdDate: updatedDate,
            updatedDate: updatedDate,
          },
        };
        let response;
        if (mode === "edit") {
          response = await put(UPDATE_CONTACT_DATA_BY_ID(id), payload);
        } else {
          response = await post(CREATE_CONTACT, payload);
        }
        console.log("Save Contact API Response:", response);
        if (response && response.status) {
          showSuccessToast(
            mode === "edit"
              ? "Contact updated successfully"
              : "Contact created successfully"
          );
          navigate("/contacts");
        } else {
          throw new Error(response.message || "Failed to save contact");
        }
      } catch (error) {
        console.error("Save Contact Error:", error);
        showErrorToast("Failed to save contact");
      } finally {
        setApiStatus((prev) => ({ ...prev, loading: false }));
      }
    },
  });

  const fetchCountries = useCallback(async () => {
    try {
      setApiStatus((prev) => ({ ...prev, loading: true }));
      const fetchedCountries = getAllCountries();
      setCountries(fetchedCountries || []);
    } catch (error) {
      console.error("Fetch Countries Error:", error);
      showErrorToast("Failed to load countries");
      setApiStatus((prev) => ({ ...prev, error: "Failed to load countries" }));
    } finally {
      setApiStatus((prev) => ({ ...prev, loading: false }));
    }
  }, []);

  const fetchStates = useCallback(async (countryCode) => {
    if (!countryCode) {
      setStates([]);
      return;
    }
    try {
      setApiStatus((prev) => ({ ...prev, loading: true }));
      const fetchedStates = getStatesForCountry(countryCode);
      setStates(fetchedStates || []);
    } catch (error) {
      console.error("Fetch States Error:", error);
      showErrorToast("Failed to load states");
      setApiStatus((prev) => ({ ...prev, error: "Failed to load states" }));
      setStates([]);
    } finally {
      setApiStatus((prev) => ({ ...prev, loading: false }));
    }
  }, []);

  const fetchCities = useCallback(async (countryCode, stateCode) => {
    if (!stateCode || !countryCode) {
      console.log("fetchCities skipped: No stateCode or country", {
        stateCode,
        country: countryCode,
      });
      setCities([]);
      return;
    }
    try {
      setApiStatus((prev) => ({ ...prev, loading: true }));
      console.log("Fetching cities for:", {
        country: countryCode,
        state: stateCode,
      });
      const fetchedCities = await getCitiesForState(countryCode, stateCode);
      setCities(fetchedCities || []);
    } catch (error) {
      console.error("Fetch Cities Error:", error);
      showErrorToast("Failed to load cities");
      setApiStatus((prev) => ({ ...prev, error: "Failed to load cities" }));
      setCities([]);
    } finally {
      setApiStatus((prev) => ({ ...prev, loading: false }));
    }
  }, []);

  const fetchVendors = useCallback(async () => {
    try {
      setApiStatus((prev) => ({ ...prev, loading: true }));
      const response = await get(api.GET_ALL_VENDORS_LIGHT_API);
      console.log("Vendors Light API Response:", response);
      if (response && response.status && Array.isArray(response.data)) {
        const updatedData = response.data.map((item) => ({
          value: String(item.vendorId) || "",
          label:
            item.companyName && item.companyName.trim() !== ""
              ? `${item.companyName.trim()} - (${item.vendorCode || "N/A"})`
              : `${item.email || item.vendorCode || "N/A"} - (${item.vendorCode || "N/A"})`,
          companyName: item.companyName || "",
          primaryContactName: item.primaryContactName || "",
        }));
        console.log("Formatted Vendors:", updatedData);
        setVendors(updatedData);
      } else {
        throw new Error("Invalid vendor response");
      }
    } catch (error) {
      console.error("Fetch Vendors Error:", error);
      showErrorToast("Failed to load vendors");
      setApiStatus((prev) => ({ ...prev, error: "Failed to load vendors" }));
      setVendors([]);
    } finally {
      setApiStatus((prev) => ({ ...prev, loading: false }));
    }
  }, []);

  const fetchCustomers = useCallback(async () => {
    try {
      setApiStatus((prev) => ({ ...prev, loading: true }));
      const response = await get(api.GET_ALL_CUSTOMERS_LIGHT_API);
      console.log("Customers API Response:", response);
      
        const updatedCustData = response.map((item) => ({
          value: String(item.customerId) || "",
          label:
            item.companyName && item.companyName.trim() !== ""
              ? `${item.companyName.trim()} - (${item.customerCode || "N/A"})`
              : `${item.email || item.customerCode || "N/A"} - (${item.customerCode || "N/A"})`,
          companyName: item.companyName || "",
          primaryContactName: item.primaryContactName || "",
        }));
        console.log("Formatted Customers:", updatedCustData);
        setCustomers(updatedCustData);
      
    } catch (error) {
      console.error("Fetch Customers Error:", error);
      showErrorToast("Failed to load customers");
      setApiStatus((prev) => ({ ...prev, error: "Failed to load customers" }));
      setCustomers([]);
    } finally {
      setApiStatus((prev) => ({ ...prev, loading: false }));
    }
  }, []);

  const fetchContactData = useCallback(async () => {
    if (!id || id === "undefined" || fetchContactFailed) {
      console.log("fetchContactData skipped:", { id, fetchContactFailed });
      return;
    }
    try {
      setApiStatus((prev) => ({ ...prev, loading: true }));
      console.log("Fetching contact data for id:", id);
      const response = await get(GET_CONTACT_BY_ID(id));
      console.log("Contact API Response:", response);
      if (response && response.status && response.data) {
        const contact = response.data;
        validation.setValues({
          contactRelatedTo: contact.contactRelatedTo?.toLowerCase() || "",
          customerName: contact.customerName || "",
          customerId: String(contact.customerId) || "",
          vendorName: contact.vendorName || "",
          vendorId: String(contact.vendorId) || "",
          contactId: String(contact.id) || "",
          firstName: contact.firstName || "",
          lastName: contact.lastName || "",
          department: contact.department || "",
          designation: contact.designation || "",
          primaryContact: contact.primaryContact === "YES",
          status: contact.status === 1 ? "active" : "inactive",
          contactType: contact.contactType || "",
          notes: contact.notes || "",
          createdBy: contact.createdBy || user?.email || "unknown",
          createdDate: contact.createdDate || updatedDate,
          updatedDate: contact.updatedDate || updatedDate,
          email: contact.email || "",
          mobile: contact.phoneNumber || "",
          landline: contact.landLine || "",
          linkedIn: contact.linkedInUrl || "",
          country: contact.country || "",
          province: contact.province || "",
          city: contact.city || "",
          postalCode: String(contact.postalCode) || "",
          address: contact.address || "",
          isNavigate: false,
        });
        setRelation(contact.contactRelatedTo?.toLowerCase() || "");
        if (contact.country) {
          await fetchStates(contact.country);
          if (contact.province) {
            await fetchCities(contact.country, contact.province);
          }
        }
      } else {
        throw new Error(response?.message || "Failed to fetch contact data");
      }
    } catch (error) {
      console.error("Fetch Contact Error:", error);
      showErrorToast("Failed to load contact data");
      setApiStatus((prev) => ({ ...prev, error: "Failed to load contact data" }));
      setFetchContactFailed(true);
    } finally {
      setApiStatus((prev) => ({ ...prev, loading: false }));
    }
  }, [id, fetchStates, fetchCities, user?.email, updatedDate, fetchContactFailed]);

  useEffect(() => {
    fetchCountries();
    fetchVendors();
    fetchCustomers();
    if (id) {
      fetchContactData();
    }
  }, [fetchCountries, fetchVendors, fetchCustomers, fetchContactData, id]);

  const handleCountryChange = useCallback(
    (e) => {
      const selectedCountry = e.target.value;
      validation.setFieldValue("country", selectedCountry);
      validation.setFieldValue("province", "");
      validation.setFieldValue("city", "");
      setStates([]);
      setCities([]);
      fetchStates(selectedCountry);
    },
    [validation, fetchStates]
  );

  const handleStateChange = useCallback(
    (e) => {
      const selectedState = e.target.value;
      validation.setFieldValue("province", selectedState);
      validation.setFieldValue("city", "");
      setCities([]);
      fetchCities(validation.values.country, selectedState);
    },
    [validation, fetchCities]
  );

  const handleRelationChange = useCallback(
    (e) => {
      const value = e.target.value;
      setRelation(value);
      validation.handleChange(e);
      validation.setFieldValue("customerName", "");
      validation.setFieldValue("customerId", "");
      validation.setFieldValue("vendorName", "");
      validation.setFieldValue("vendorId", "");
      validation.setFieldValue("primaryContact", false);
    },
    [validation]
  );

  const handleVendorChange = useCallback(
    (e) => {
      const selectedVendorId = e.target.value;
      validation.setFieldValue("vendorId", selectedVendorId);
      const selectedVendor = vendors.find((v) => v.value === selectedVendorId);
      const primaryContact = selectedVendor?.primaryContactName?.trim() ? true : false;
      validation.setFieldValue("vendorName", selectedVendor?.companyName || "");
      validation.setFieldValue("primaryContact", primaryContact);
      console.log(
        "Selected Vendor:",
        selectedVendor,
        "Primary Contact Name:",
        selectedVendor?.primaryContactName,
        "Primary Contact Set:",
        primaryContact
      );
    },
    [validation, vendors]
  );

  const handleCustomerChange = useCallback(
    (e) => {
      const selectedCustomerId = e.target.value;
      validation.setFieldValue("customerId", selectedCustomerId);
      const selectedCustomer = customers.find((c) => c.value === selectedCustomerId);
      const primaryContact = selectedCustomer?.primaryContactName?.trim() ? true : false;
      validation.setFieldValue("customerName", selectedCustomer?.companyName || "");
      validation.setFieldValue("primaryContact", primaryContact);
      console.log(
        "Selected Customer:",
        selectedCustomer,
        "Primary Contact Name:",
        selectedCustomer?.primaryContactName,
        "Primary Contact Set:",
        primaryContact
      );
    },
    [validation, customers]
  );

  const handleAddress = async () => {
    const errors = await validation.validateForm();
    if (Object.keys(errors).length > 0) {
      showErrorToast("Please fill all required fields correctly.");
      return;
    }
    validation.setFieldValue("isNavigate", true);
    validation.handleSubmit();
  };

  const heading = mode === "edit" ? "Update Contact" : "Add Contact";

  return (
    <div className="page-content">
      <Container fluid style={{ padding: "0px", gap: "10px" }}>
        <PageSubHeader
          heading={heading}
          routePath="Home"
          pageName="Contacts"
          backPath="/contacts"
        />
        <Row className="p-4">
          <Col>
            <Card>
              <CardBody>
                {apiStatus.loading && (
                  <div className="text-center my-3">
                    <LoadingSpinner />
                  </div>
                )}
                <Form
                  className="row g-3 needs-validation py-4"
                  onSubmit={(e) => {
                    e.preventDefault();
                    validation.handleSubmit();
                    return false;
                  }}
                >
                  <Row className="card-group">
                    <Col xs={12} className="card-header">
                      <p className="form-header">Basic Information</p>
                    </Col>
                    <Row className="card-group-body">
                      <Col md={6}>
                        <FormGroup className="mb-3">
                          <Label htmlFor="contactRelatedTo">
                            Contact Related To{" "}
                            <span className="text-danger">*</span>
                          </Label>
                          <Input
                            type="select"
                            id="contactRelatedTo"
                            name="contactRelatedTo"
                            className="form-select"
                            onChange={
                              mode === "view" ? undefined : handleRelationChange
                            }
                            onBlur={
                              mode === "view"
                                ? undefined
                                : validation.handleBlur
                            }
                            value={validation.values.contactRelatedTo || ""}
                            invalid={
                              validation.touched.contactRelatedTo &&
                              validation.errors.contactRelatedTo
                            }
                            disabled={mode === "view"}
                          >
                            <option value="">Select</option>
                            <option value="customer">Customer</option>
                            <option value="vendor">Vendor</option>
                          </Input>
                          {validation.touched.contactRelatedTo &&
                            validation.errors.contactRelatedTo && (
                              <FormFeedback>
                                {validation.errors.contactRelatedTo}
                              </FormFeedback>
                            )}
                        </FormGroup>
                      </Col>
                      {relation === "customer" && (
                        <Col md={6}>
                          <FormGroup className="mb-3">
                            <Label htmlFor="customerId">
                              Customer Name{" "}
                              <span className="text-danger">*</span>
                            </Label>
                            <Input
                              type="select"
                              id="customerId"
                              name="customerId"
                              className="form-select"
                              onChange={
                                mode === "view"
                                  ? undefined
                                  : handleCustomerChange
                              }
                              onBlur={
                                mode === "view"
                                  ? undefined
                                  : validation.handleBlur
                              }
                              value={validation.values.customerId || ""}
                              invalid={
                                validation.touched.customerId &&
                                validation.errors.customerId
                              }
                              disabled={mode === "view"}
                            >
                              <option value="">Select Customer</option>
                              {customers.map((customer) => (
                                <option
                                  key={customer.value}
                                  value={customer.value}
                                >
                                  {customer.label}
                                </option>
                              ))}
                            </Input>
                            {validation.touched.customerId &&
                              validation.errors.customerId && (
                                <FormFeedback>
                                  {validation.errors.customerId}
                                </FormFeedback>
                              )}
                          </FormGroup>
                        </Col>
                      )}
                      {relation === "vendor" && (
                        <Col md={6}>
                          <FormGroup className="mb-3">
                            <Label htmlFor="vendorId">
                              Vendor Name <span className="text-danger">*</span>
                            </Label>
                            <Input
                              type="select"
                              id="vendorId"
                              name="vendorId"
                              className="form-select"
                              onChange={
                                mode === "view" ? undefined : handleVendorChange
                              }
                              onBlur={
                                mode === "view"
                                  ? undefined
                                  : validation.handleBlur
                              }
                              value={validation.values.vendorId || ""}
                              invalid={
                                validation.touched.vendorId &&
                                validation.errors.vendorId
                              }
                              disabled={mode === "view"}
                            >
                              <option value="">Select Vendor</option>
                              {vendors.map((vendor) => (
                                <option
                                  key={vendor.value}
                                  value={vendor.value}
                                >
                                  {vendor.label}
                                </option>
                              ))}
                            </Input>
                            {validation.touched.vendorId &&
                              validation.errors.vendorId && (
                                <FormFeedback>
                                  {validation.errors.vendorId}
                                </FormFeedback>
                              )}
                          </FormGroup>
                        </Col>
                      )}
                      <Col md={6}>
                        <FormGroup className="mb-3">
                          <Label htmlFor="contactId">Contact ID</Label>
                          <Input
                            name="contactId"
                            type="text"
                            className="form-control"
                            id="contactId"
                            value={
                              validation.values.contactId || "Auto-generated"
                            }
                            disabled
                          />
                        </FormGroup>
                      </Col>
                      <Col md={6}>
                        <FormGroup className="mb-3">
                          <Label htmlFor="firstName">
                            First Name <span className="text-danger">*</span>
                          </Label>
                          <Input
                            name="firstName"
                            type="text"
                            className="form-control"
                            id="firstName"
                            onChange={
                              mode === "view"
                                ? undefined
                                : validation.handleChange
                            }
                            onBlur={
                              mode === "view"
                                ? undefined
                                : validation.handleBlur
                            }
                            value={validation.values.firstName || ""}
                            invalid={
                              validation.touched.firstName &&
                              validation.errors.firstName
                            }
                            disabled={mode === "view"}
                          />
                          {validation.touched.firstName &&
                            validation.errors.firstName && (
                              <FormFeedback>
                                {validation.errors.firstName}
                              </FormFeedback>
                            )}
                        </FormGroup>
                      </Col>
                      <Col md={6}>
                        <FormGroup className="mb-3">
                          <Label htmlFor="lastName">
                            Last Name <span className="text-danger">*</span>
                          </Label>
                          <Input
                            name="lastName"
                            type="text"
                            className="form-control"
                            id="lastName"
                            onChange={
                              mode === "view"
                                ? undefined
                                : validation.handleChange
                            }
                            onBlur={
                              mode === "view"
                                ? undefined
                                : validation.handleBlur
                            }
                            value={validation.values.lastName || ""}
                            invalid={
                              validation.touched.lastName &&
                              validation.errors.lastName
                            }
                            disabled={mode === "view"}
                          />
                          {validation.touched.lastName &&
                            validation.errors.lastName && (
                              <FormFeedback>
                                {validation.errors.lastName}
                              </FormFeedback>
                            )}
                        </FormGroup>
                      </Col>
                      <Col md={6}>
                        <FormGroup className="mb-3">
                          <Label htmlFor="department">Department</Label>
                          <Input
                            name="department"
                            type="text"
                            className="form-control"
                            id="department"
                            onChange={
                              mode === "view"
                                ? undefined
                                : validation.handleChange
                            }
                            onBlur={
                              mode === "view"
                                ? undefined
                                : validation.handleBlur
                            }
                            value={validation.values.department || ""}
                            disabled={mode === "view"}
                          />
                        </FormGroup>
                      </Col>
                      <Col md={6}>
                        <FormGroup className="mb-3">
                          <Label htmlFor="designation">Designation</Label>
                          <Input
                            name="designation"
                            type="text"
                            className="form-control"
                            id="designation"
                            onChange={
                              mode === "view"
                                ? undefined
                                : validation.handleChange
                            }
                            onBlur={
                              mode === "view"
                                ? undefined
                                : validation.handleBlur
                            }
                            value={validation.values.designation || ""}
                            disabled={mode === "view"}
                          />
                        </FormGroup>
                      </Col>
                      <Col md={6}>
                        <FormGroup className="mb-3">
                          <Label htmlFor="primaryContact">
                            Primary Contact
                          </Label>
                          <Input
                            type="checkbox"
                            id="primaryContact"
                            name="primaryContact"
                            className="form-check-input"
                            onChange={
                              mode === "view"
                                ? undefined
                                : validation.handleChange
                            }
                            onBlur={
                              mode === "view"
                                ? undefined
                                : validation.handleBlur
                            }
                            checked={validation.values.primaryContact}
                            disabled={mode === "view"}
                          />
                          {validation.touched.primaryContact &&
                            validation.errors.primaryContact && (
                              <FormFeedback>
                                {validation.errors.primaryContact}
                              </FormFeedback>
                            )}
                        </FormGroup>
                      </Col>
                      <Col md={6}>
                        <FormGroup className="mb-3">
                          <Label htmlFor="status">
                            Status <span className="text-danger">*</span>
                          </Label>
                          <Input
                            type="select"
                            id="status"
                            name="status"
                            className="form-select"
                            onChange={
                              mode === "view"
                                ? undefined
                                : validation.handleChange
                            }
                            onBlur={
                              mode === "view"
                                ? undefined
                                : validation.handleBlur
                            }
                            value={validation.values.status || ""}
                            invalid={
                              validation.touched.status &&
                              validation.errors.status
                            }
                            disabled={mode === "view"}
                          >
                            <option value="active">Active</option>
                            <option value="inactive">Inactive</option>
                          </Input>
                          {validation.touched.status &&
                            validation.errors.status && (
                              <FormFeedback>
                                {validation.errors.status}
                              </FormFeedback>
                            )}
                        </FormGroup>
                      </Col>
                      <Col md={6}>
                        <FormGroup className="mb-3">
                          <Label htmlFor="contactType">
                            Contact Type <span className="text-danger">*</span>
                          </Label>
                          <Input
                            type="select"
                            id="contactType"
                            name="contactType"
                            className="form-select"
                            onChange={
                              mode === "view"
                                ? undefined
                                : validation.handleChange
                            }
                            onBlur={
                              mode === "view"
                                ? undefined
                                : validation.handleBlur
                            }
                            value={validation.values.contactType || ""}
                            invalid={
                              validation.touched.contactType &&
                              validation.errors.contactType
                            }
                            disabled={mode === "view"}
                          >
                            <option value="">Select</option>
                            <option value="Decision Maker">
                              Decision Maker
                            </option>
                            <option value="Influencer">Influencer</option>
                            <option value="End User">End User</option>
                            <option value="Billing Contact">
                              Billing Contact
                            </option>
                          </Input>
                          {validation.touched.contactType &&
                            validation.errors.contactType && (
                              <FormFeedback>
                                {validation.errors.contactType}
                              </FormFeedback>
                            )}
                        </FormGroup>
                      </Col>
                      <Col md={6}>
                        <FormGroup className="mb-3">
                          <Label htmlFor="notes">Notes</Label>
                          <Input
                            name="notes"
                            type="textarea"
                            className="form-control"
                            id="notes"
                            onChange={
                              mode === "view"
                                ? undefined
                                : validation.handleChange
                            }
                            onBlur={
                              mode === "view"
                                ? undefined
                                : validation.handleBlur
                            }
                            value={validation.values.notes || ""}
                            disabled={mode === "view"}
                          />
                        </FormGroup>
                      </Col>
                    </Row>
                  </Row>

                  <Row className="card-group mt-4">
                    <Col xs={12} className="card-header">
                      <p className="form-header">Contact Details</p>
                    </Col>
                    <Row className="card-group-body">
                      <Col md={6}>
                        <FormGroup className="mb-3">
                          <Label htmlFor="email">
                            Email Address <span className="text-danger">*</span>
                          </Label>
                          <Input
                            name="email"
                            type="email"
                            className="form-control"
                            id="email"
                            onChange={
                              mode === "view"
                                ? undefined
                                : validation.handleChange
                            }
                            onBlur={
                              mode === "view"
                                ? undefined
                                : validation.handleBlur
                            }
                            value={validation.values.email || ""}
                            invalid={
                              validation.touched.email &&
                              validation.errors.email
                            }
                            disabled={mode === "view"}
                          />
                          {validation.touched.email &&
                            validation.errors.email && (
                              <FormFeedback>
                                {validation.errors.email}
                              </FormFeedback>
                            )}
                        </FormGroup>
                      </Col>
                      <Col md={6}>
                        <FormGroup className="mb-3">
                          <Label htmlFor="mobile">
                            Phone Number (Mobile){" "}
                            <span className="text-danger">*</span>
                          </Label>
                          <PhoneInput
                            international
                            defaultCountry="IN"
                            id="mobile"
                            name="mobile"
                            className="form-control d-flex"
                            placeholder="Enter mobile number"
                            onChange={(value) =>
                              mode !== "view" &&
                              validation.setFieldValue("mobile", value)
                            }
                            onBlur={
                              mode === "view"
                                ? undefined
                                : validation.handleBlur
                            }
                            value={validation.values.mobile}
                            invalid={
                              validation.touched.mobile &&
                              validation.errors.mobile
                            }
                            disabled={mode === "view"}
                          />
                          {validation.touched.mobile &&
                            validation.errors.mobile && (
                              <FormFeedback>
                                {validation.errors.mobile}
                              </FormFeedback>
                            )}
                        </FormGroup>
                      </Col>
                      <Col md={6}>
                        <FormGroup className="mb-3">
                          <Label htmlFor="landline">
                            Phone Number (Landline)
                          </Label>
                          <PhoneInput
                            international
                            defaultCountry="IN"
                            id="landline"
                            name="landline"
                            className="form-control d-flex"
                            placeholder="Enter landline number"
                            onChange={(value) =>
                              mode !== "view" &&
                              validation.setFieldValue("landline", value)
                            }
                            onBlur={
                              mode === "view"
                                ? undefined
                                : validation.handleBlur
                            }
                            value={validation.values.landline}
                            invalid={
                              validation.touched.landline &&
                              validation.errors.landline
                            }
                            disabled={mode === "view"}
                          />
                          {validation.touched.landline &&
                            validation.errors.landline && (
                              <FormFeedback>
                                {validation.errors.landline}
                              </FormFeedback>
                            )}
                        </FormGroup>
                      </Col>
                      <Col md={6}>
                        <FormGroup className="mb-3">
                          <Label htmlFor="linkedIn">LinkedIn Profile URL</Label>
                          <Input
                            name="linkedIn"
                            type="url"
                            className="form-control"
                            id="linkedIn"
                            onChange={
                              mode === "view"
                                ? undefined
                                : validation.handleChange
                            }
                            onBlur={
                              mode === "view"
                                ? undefined
                                : validation.handleBlur
                            }
                            value={validation.values.linkedIn || ""}
                            invalid={
                              validation.touched.linkedIn &&
                              validation.errors.linkedIn
                            }
                            disabled={mode === "view"}
                          />
                          {validation.touched.linkedIn &&
                            validation.errors.linkedIn && (
                              <FormFeedback>
                                {validation.errors.linkedIn}
                              </FormFeedback>
                            )}
                        </FormGroup>
                      </Col>
                    </Row>
                  </Row>

                  <Row className="card-group mt-4">
                    <Col xs={12} className="card-header">
                      <p className="form-header">Communication Address</p>
                    </Col>
                    <Row className="card-group-body">
                      <Col md={6}>
                        <FormGroup className="mb-3">
                          <Label htmlFor="country">
                            Country <span className="text-danger">*</span>
                          </Label>
                          <Input
                            type="select"
                            id="country"
                            name="country"
                            className="form-select"
                            onChange={
                              mode === "view" ? undefined : handleCountryChange
                            }
                            onBlur={
                              mode === "view"
                                ? undefined
                                : validation.handleBlur
                            }
                            value={validation.values.country || ""}
                            invalid={
                              validation.touched.country &&
                              validation.errors.country
                            }
                            disabled={mode === "view"}
                          >
                            <option value="">Select Country</option>
                            {countries.map((country) => (
                              <option key={country.value} value={country.value}>
                                {country.label}
                              </option>
                            ))}
                          </Input>
                          {validation.touched.country &&
                            validation.errors.country && (
                              <FormFeedback>
                                {validation.errors.country}
                              </FormFeedback>
                            )}
                        </FormGroup>
                      </Col>
                      <Col md={6}>
                        <FormGroup className="mb-3">
                          <Label htmlFor="province">
                            Province <span className="text-danger">*</span>
                          </Label>
                          <Input
                            type="select"
                            id="province"
                            name="province"
                            className="form-select"
                            onChange={
                              mode === "view" ? undefined : handleStateChange
                            }
                            onBlur={
                              mode === "view"
                                ? undefined
                                : validation.handleBlur
                            }
                            value={validation.values.province || ""}
                            invalid={
                              validation.touched.province &&
                              validation.errors.province
                            }
                            disabled={
                              mode === "view" || !validation.values.country
                            }
                          >
                            <option value="">Select Province</option>
                            {states.map((state) => (
                              <option key={state.value} value={state.value}>
                                {state.label}
                              </option>
                            ))}
                          </Input>
                          {validation.touched.province &&
                            validation.errors.province && (
                              <FormFeedback>
                                {validation.errors.province}
                              </FormFeedback>
                            )}
                        </FormGroup>
                      </Col>
                      <Col md={6}>
                        <FormGroup className="mb-3">
                          <Label htmlFor="city">
                            City <span className="text-danger">*</span>
                          </Label>
                          <Input
                            type="select"
                            id="city"
                            name="city"
                            className="form-select"
                            onChange={
                              mode === "view"
                                ? undefined
                                : validation.handleChange
                            }
                            onBlur={
                              mode === "view"
                                ? undefined
                                : validation.handleBlur
                            }
                            value={validation.values.city || ""}
                            invalid={
                              validation.touched.city && validation.errors.city
                            }
                            disabled={
                              mode === "view" || !validation.values.province
                            }
                          >
                            <option value="">Select City</option>
                            {cities.map((city) => (
                              <option key={city.value} value={city.value}>
                                {city.label}
                              </option>
                            ))}
                          </Input>
                          {validation.touched.city &&
                            validation.errors.city && (
                              <FormFeedback>
                                {validation.errors.city}
                              </FormFeedback>
                            )}
                        </FormGroup>
                      </Col>
                      <Col md={6}>
                        <FormGroup className="mb-3">
                          <Label htmlFor="postalCode">
                            Postal Code <span className="text-danger">*</span>
                          </Label>
                          <Input
                            name="postalCode"
                            type="text"
                            className="form-control"
                            id="postalCode"
                            onChange={
                              mode === "view"
                                ? undefined
                                : validation.handleChange
                            }
                            onBlur={
                              mode === "view"
                                ? undefined
                                : validation.handleBlur
                            }
                            value={validation.values.postalCode || ""}
                            invalid={
                              validation.touched.postalCode &&
                              validation.errors.postalCode
                            }
                            disabled={mode === "view"}
                          />
                          {validation.touched.postalCode &&
                            validation.errors.postalCode && (
                              <FormFeedback>
                                {validation.errors.postalCode}
                              </FormFeedback>
                            )}
                        </FormGroup>
                      </Col>
                      <Col md={12}>
                        <FormGroup className="mb-3">
                          <Label htmlFor="address">
                            Address <span className="text-danger">*</span>
                          </Label>
                          <Input
                            name="address"
                            type="textarea"
                            className="form-control"
                            id="address"
                            onChange={
                              mode === "view"
                                ? undefined
                                : validation.handleChange
                            }
                            onBlur={
                              mode === "view"
                                ? undefined
                                : validation.handleBlur
                            }
                            value={validation.values.address || ""}
                            invalid={
                              validation.touched.address &&
                              validation.errors.address
                            }
                            disabled={mode === "view"}
                          />
                          {validation.touched.address &&
                            validation.errors.address && (
                              <FormFeedback>
                                {validation.errors.address}
                              </FormFeedback>
                            )}
                        </FormGroup>
                      </Col>
                    </Row>
                  </Row>
                </Form>
              </CardBody>
            </Card>
          </Col>
        </Row>
        <Footer
          save={validation.handleSubmit}
          saveText={mode === "view" ? "Back" : "Save"}
          disabled={apiStatus.loading}
         
        />
      </Container>
    </div>
  );
}

export default AddContact;